// src/pages/ReportsPage.tsx — the Reports applet (route /reports).
//
// Loads instantly from the daily snapshot (GET /api/reports/snapshot) — no live
// call to reports.nymr.co.uk. Basic view = the seven measures (everyone with
// apps.reports); the Executive tab appears only when canExec (apps.reportsExec).
// Every measure carries a home-pin (limit 1, radio-like). Ported from the
// prototype reports-app.jsx; matches it 1:1.

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { C, F } from "../theme";
import { TopBar } from "../components/ui/TopBar";
import { Card } from "../components/ui/Card";
import { Stamp } from "../components/ui/Stamp";
import { RIcon } from "../components/reports/reportsIcons";
import { MeasureCard, BigFig } from "../components/reports/MeasureCard";
import { RevenueBars } from "../components/reports/RevenueBars";
import { CapacityGauge } from "../components/reports/CapacityGauge";
import {
  getReportsSnapshot,
  getReportsPin,
  putReportsPin,
  fmtGBP,
  fmtNum,
  yoy,
} from "../lib/reports";
import type { MeasureId, ReportsSnapshot, SnapshotResponse } from "../lib/reports";

// Freshness chip — driven from the snapshot, never hard-coded. Green dot when
// the snapshot is from today; amber + the date when it's stale (job missed).
function RefreshChip({ refreshedAt, snapshotDate }: { refreshedAt: string | null; snapshotDate: string | null }) {
  const todayStr = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD, local
  const isToday = !!snapshotDate && snapshotDate === todayStr;
  const time = refreshedAt ? new Date(refreshedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "";
  const dayLabel = snapshotDate ? new Date(snapshotDate + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "";
  const bg = isToday ? C.sageBg : C.brassBg;
  const bd = isToday ? C.sageLt : C.brassLt;
  const fg = isToday ? C.sageDk : C.brassDk;
  const text = !refreshedAt ? "First refresh pending" : isToday ? `Data refreshed today at ${time}` : `Data from ${dayLabel} at ${time}`;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 11px 6px 9px", borderRadius: 10, background: bg, border: `1px solid ${bd}` }}>
      <span style={{ width: 7, height: 7, borderRadius: 7, background: fg, boxShadow: isToday ? "0 0 7px rgba(79,104,82,0.6)" : "none", flexShrink: 0 }} />
      <span style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 800, color: fg, letterSpacing: 0.2 }}>{text}</span>
    </div>
  );
}

// Names of the events contributing to an Events-net figure (name + compact net).
function EventList({ items }: { items: { name: string; net: number }[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 3 }}>
      {items.map((it) => (
        <div key={it.name} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.inkSoft }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{it.name}</span>
          <span style={{ color: C.inkMute, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{fmtGBP(it.net, { compact: true })}</span>
        </div>
      ))}
    </div>
  );
}

function ReportsBasic({ s, pinnedId, onPin }: { s: ReportsSnapshot; pinnedId: MeasureId | null; onPin: (id: MeasureId | null) => void }) {
  const nr = s.netRevenue;
  const ga = s.giftAid;
  const ff = s.footfall;
  const pm = s.pullman;
  const ev = s.events;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Net revenue — hero */}
      <MeasureCard measureId="net-revenue" icon={RIcon.Coins} label="Net revenue" tone="brass" pinnedId={pinnedId} onPin={onPin}>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 130px" }}>
            <BigFig value={fmtGBP(nr.fyToDate, { compact: true })} sub={s.fyLabel + " · to date"} delta={yoy(nr.fyToDate, nr.fyPrevToDate)} />
          </div>
          <div style={{ width: 1, alignSelf: "stretch", background: C.hairlineLt }} />
          <div style={{ flex: "1 1 110px" }}>
            <div style={{ fontFamily: F.serif, fontSize: 26, fontWeight: 700, color: C.inkSoft, letterSpacing: -0.5, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{fmtGBP(nr.inclForward, { compact: true })}</div>
            <div style={{ fontFamily: F.body, fontSize: 12, fontWeight: 700, color: C.inkMute, letterSpacing: 0.4, textTransform: "uppercase", marginTop: 6 }}>Inc. forward bookings</div>
          </div>
        </div>
      </MeasureCard>

      {/* Gift Aid + Footfall */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <MeasureCard measureId="gift-aid" icon={RIcon.GiftAid} label="Gift Aid" tone="sage" pinnedId={pinnedId} onPin={onPin}>
          <BigFig value={fmtGBP(ga.fyToDate, { compact: true })} sub={s.fyLabel + " · to date"} delta={yoy(ga.fyToDate, ga.fyPrevToDate)} />
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.hairlineLt}` }}>
            <div style={{ fontFamily: F.serif, fontSize: 22, fontWeight: 700, color: C.inkSoft, letterSpacing: -0.4, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{fmtGBP(ga.inclForward, { compact: true })}</div>
            <div style={{ fontFamily: F.body, fontSize: 11.5, fontWeight: 700, color: C.inkMute, letterSpacing: 0.3, textTransform: "uppercase", marginTop: 5 }}>Inc. forward bookings</div>
          </div>
        </MeasureCard>
        <MeasureCard measureId="footfall" icon={RIcon.Footfall} label="Footfall" tone="heather" pinnedId={pinnedId} onPin={onPin}>
          <BigFig value={fmtNum(ff.fyToDate)} sub="Passengers" delta={yoy(ff.fyToDate, ff.fyPrevToDate)} />
        </MeasureCard>
      </div>

      {/* Revenue YoY */}
      <MeasureCard measureId="revenue-yoy" icon={RIcon.Bars} label="Revenue — this year vs last" tone="brass" pinnedId={pinnedId} onPin={onPin}>
        <RevenueBars data={s.revenueByMonth} />
      </MeasureCard>

      {/* Pullman net */}
      <MeasureCard measureId="pullman-net" icon={RIcon.Pullman} label="Pullman net" tone="heather" pinnedId={pinnedId} onPin={onPin}>
        <div style={{ display: "flex", gap: 14 }}>
          <div style={{ flex: 1 }}>
            <BigFig value={fmtGBP(pm.netFyToDate, { compact: true })} sub={s.fyLabel + " · to date"} />
          </div>
          <div style={{ width: 1, background: C.hairlineLt }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: F.serif, fontSize: 26, fontWeight: 700, color: C.inkSoft, letterSpacing: -0.5, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{fmtGBP(pm.netThisMonth, { compact: true })}</div>
            <div style={{ fontFamily: F.body, fontSize: 12, fontWeight: 700, color: C.inkMute, letterSpacing: 0.4, textTransform: "uppercase", marginTop: 6 }}>{pm.monthLabel} · so far</div>
          </div>
        </div>
      </MeasureCard>

      {/* Pullman capacity */}
      <MeasureCard measureId="pullman-capacity" icon={RIcon.Gauge} label="Pullman capacity — next 30 days" tone="sage" pinnedId={pinnedId} onPin={onPin}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <CapacityGauge value={pm.capacityNext30} size={132} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.inkSoft, lineHeight: 1.5 }}>
              <strong style={{ color: C.ink, fontWeight: 800 }}>{fmtNum(pm.seatsBooked)}</strong> of {fmtNum(pm.seatsAvailable)} seats sold across <strong style={{ color: C.ink, fontWeight: 800 }}>{pm.servicesNext30}</strong> scheduled services.
            </div>
          </div>
        </div>
      </MeasureCard>

      {/* Events net — recent actual / upcoming booked split */}
      <MeasureCard measureId="events-net" icon={RIcon.Event} label="Events net" tone="brass" pinnedId={pinnedId} onPin={onPin}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
          <div style={{ paddingRight: 14 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
              <Stamp tone="heather">LAST 30 DAYS</Stamp>
            </div>
            <div style={{ fontFamily: F.serif, fontSize: 27, fontWeight: 800, color: C.ink, letterSpacing: -0.5, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{fmtGBP(ev.last30Net, { compact: true })}</div>
            <div style={{ fontFamily: F.body, fontSize: 11.5, fontWeight: 700, color: C.inkMute, marginTop: 5 }}>{ev.last30Count} events · actual</div>
            <EventList items={ev.last30Items} />
          </div>
          <div style={{ paddingLeft: 14, borderLeft: `1px solid ${C.hairlineLt}` }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
              <Stamp tone="sage">UPCOMING</Stamp>
            </div>
            <div style={{ fontFamily: F.serif, fontSize: 27, fontWeight: 800, color: C.ink, letterSpacing: -0.5, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{fmtGBP(ev.next30Net, { compact: true })}</div>
            <div style={{ fontFamily: F.body, fontSize: 11.5, fontWeight: 700, color: C.inkMute, marginTop: 5 }}>{ev.next30Count} events · booked ahead</div>
            <EventList items={ev.next30Items} />
          </div>
        </div>
      </MeasureCard>

      <div style={{ textAlign: "center", padding: "4px 0 2px", fontFamily: F.serif, fontStyle: "italic", fontSize: 12, color: C.inkMute, lineHeight: 1.5 }}>
        Figures sourced from {s.generatedBy}.<br />Pin one measure to keep it on your home page.
      </div>
    </div>
  );
}

// Executive tab — gated by canExec; contents specified separately (stub).
function ReportsExec() {
  return (
    <div style={{ padding: "26px 4px" }}>
      <Card>
        <div style={{ textAlign: "center", padding: "20px 8px" }}>
          <div style={{ width: 60, height: 60, borderRadius: 16, background: C.heatherBg, border: `1px solid ${C.heatherLt}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <RIcon.Bars s={28} c={C.heatherDk} />
          </div>
          <div style={{ display: "inline-block", marginBottom: 10 }}><Stamp tone="heather">EXECUTIVE</Stamp></div>
          <h2 style={{ fontFamily: F.serif, fontSize: 23, fontWeight: 700, color: C.ink, margin: "0 0 8px", letterSpacing: -0.4 }}>Senior leadership reporting</h2>
          <p style={{ fontFamily: F.body, fontSize: 14, fontWeight: 600, color: C.inkSoft, lineHeight: 1.55, margin: "0 auto", maxWidth: 280 }}>
            This tab is visible to you because you hold the Executive reporting permission. Its contents are being specified separately — Chris will advise.
          </p>
        </div>
      </Card>
    </div>
  );
}

export function ReportsPage() {
  const navigate = useNavigate();
  const [resp, setResp] = useState<SnapshotResponse | null>(null);
  const [pin, setPin] = useState<MeasureId | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<"basic" | "exec">("basic");
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [snap, p] = await Promise.all([getReportsSnapshot(), getReportsPin()]);
        if (!alive) return;
        setResp(snap);
        setPin(p.pin);
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : "Could not load reports.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2000);
  }, []);

  const handlePin = useCallback(
    (id: MeasureId | null) => {
      setPin((prev) => {
        putReportsPin(id).catch(() => {
          setPin(prev);
          showToast("Could not save your pin");
        });
        return id;
      });
      showToast(id ? "Pinned to your home page" : "Removed from your home page");
    },
    [showToast],
  );

  const payload = resp?.payload ?? null;
  const canExec = resp?.canExec ?? false;
  const sub = payload ? `${payload.fyLabel} · ${payload.asOf}` : "Daily trading at a glance";

  return (
    <div style={{ position: "relative", minHeight: "100%", background: C.paper }}>
      <TopBar title="Reports" sub={sub} onBack={() => navigate(-1)} />

      {loading ? (
        <div style={{ padding: "40px 16px", textAlign: "center", fontFamily: F.body, fontSize: 14, fontWeight: 700, color: C.inkMute }}>Loading…</div>
      ) : err ? (
        <div style={{ padding: "20px 16px" }}>
          <Card>
            <div style={{ fontFamily: F.body, fontSize: 14, fontWeight: 700, color: C.signalDk }}>{err}</div>
          </Card>
        </div>
      ) : (
        <>
          <div style={{ padding: "13px 16px 0", display: "flex", justifyContent: "center" }}>
            <RefreshChip refreshedAt={resp?.refreshedAt ?? null} snapshotDate={resp?.snapshotDate ?? null} />
          </div>

          {canExec && (
            <div style={{ padding: "14px 16px 0" }}>
              <div style={{ display: "flex", gap: 4, background: C.paperWarm, border: `1px solid ${C.hairlineLt}`, borderRadius: 12, padding: 4 }}>
                {([["basic", "Reports"], ["exec", "Executive"]] as const).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setTab(id)}
                    style={{
                      flex: 1,
                      padding: "9px 8px",
                      borderRadius: 9,
                      border: "none",
                      cursor: "pointer",
                      background: tab === id ? C.card : "transparent",
                      color: tab === id ? C.brassDk : C.inkMute,
                      fontFamily: F.body,
                      fontWeight: 800,
                      fontSize: 14,
                      letterSpacing: 0.2,
                      boxShadow: tab === id ? C.shadowS : "none",
                      transition: "all 0.15s",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={{ padding: "14px 16px 28px" }}>
            {tab === "exec" && canExec ? (
              <ReportsExec />
            ) : payload ? (
              <ReportsBasic s={payload} pinnedId={pin} onPin={handlePin} />
            ) : (
              <Card>
                <div style={{ textAlign: "center", padding: "22px 10px" }}>
                  <div style={{ display: "inline-block", marginBottom: 10 }}><Stamp tone="brass">PENDING</Stamp></div>
                  <h2 style={{ fontFamily: F.serif, fontSize: 20, fontWeight: 700, color: C.ink, margin: "0 0 6px" }}>First refresh pending</h2>
                  <p style={{ fontFamily: F.body, fontSize: 13.5, fontWeight: 600, color: C.inkSoft, lineHeight: 1.5, margin: "0 auto", maxWidth: 280 }}>
                    The overnight job hasn&apos;t written a snapshot yet. Today&apos;s figures will appear here once it runs.
                  </p>
                </div>
              </Card>
            )}
          </div>
        </>
      )}

      {/* Toast */}
      <div
        style={{
          position: "fixed",
          left: "50%",
          bottom: 24,
          transform: `translateX(-50%) translateY(${toast ? 0 : 16}px)`,
          opacity: toast ? 1 : 0,
          pointerEvents: "none",
          transition: "all 0.22s",
          zIndex: 300,
          background: C.ink,
          color: C.inkOnDark,
          fontFamily: F.body,
          fontWeight: 700,
          fontSize: 13.5,
          padding: "11px 16px",
          borderRadius: 12,
          boxShadow: C.shadowL,
          display: "flex",
          alignItems: "center",
          gap: 8,
          whiteSpace: "nowrap",
        }}
      >
        <RIcon.HomePin s={16} c={C.brassGlow} />
        {toast}
      </div>
    </div>
  );
}
